import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IDiscussionComment,
  IDiscussionPagination,
  IDiscussionRoot,
  IDiscussionRootsResponse,
} from '../../interface/homeworkDiscussion.interface';
import {
  fetchDiscussionReplies,
  fetchDiscussionRoots,
  postDiscussionMessage,
} from '../action/homeworkDiscussion.action';

interface IRepliesSlot {
  items: IDiscussionComment[];
  pagination: IDiscussionPagination | null;
  loading: boolean;
  expanded: boolean;
}

interface IHomeworkDiscussionState {
  roots: {
    items: IDiscussionRoot[];
    pagination: IDiscussionPagination | null;
    loading: boolean;
    error: string | null;
    /** true when the caller is not a participant (403 on the roots GET). */
    forbidden: boolean;
    posting: boolean;
  };
  /** Lazily-loaded replies, keyed by root id. */
  repliesByRoot: Record<string, IRepliesSlot>;
}

const initialState: IHomeworkDiscussionState = {
  roots: {
    items: [],
    pagination: null,
    loading: false,
    error: null,
    forbidden: false,
    posting: false,
  },
  repliesByRoot: {},
};

const emptySlot = (): IRepliesSlot => ({
  items: [],
  pagination: null,
  loading: false,
  expanded: false,
});

// De-dupe messages by id, preserving order (keeps existing entries).
const dedupeById = <T extends { id: string }>(items: T[]): T[] => {
  const seen = new Set<string>();
  const result: T[] = [];
  items.forEach((item) => {
    if (!seen.has(item.id)) {
      seen.add(item.id);
      result.push(item);
    }
  });
  return result;
};

const homeworkDiscussionSlice = createSlice({
  name: 'homeworkDiscussion',
  initialState,
  reducers: {
    // Clear everything (e.g. on unmount / tab switch).
    resetDiscussion: () => initialState,
    // Collapse an expanded reply sub-list without discarding what was loaded.
    collapseReplies: (state, action: PayloadAction<string>) => {
      const slot = state.repliesByRoot[action.payload];
      if (slot) {
        slot.expanded = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // --- Roots ---
      .addCase(fetchDiscussionRoots.pending, (state) => {
        state.roots.loading = true;
        state.roots.error = null;
      })
      .addCase(
        fetchDiscussionRoots.fulfilled,
        (state, action: PayloadAction<IDiscussionRootsResponse>) => {
          state.roots.loading = false;
          // Full replacement each poll; dedupe defends against any duplicate ids.
          state.roots.items = dedupeById(action.payload.data.messages);
          state.roots.pagination = action.payload.data.pagination;
          state.roots.error = null;
          state.roots.forbidden = false;
        },
      )
      .addCase(fetchDiscussionRoots.rejected, (state, action) => {
        state.roots.loading = false;
        const status = action.payload?.status;
        if (status === 403) {
          state.roots.forbidden = true;
          state.roots.items = [];
          state.roots.pagination = null;
          state.roots.error = null;
        } else {
          state.roots.error = action.payload?.message || 'Failed to load the discussion.';
        }
      })
      // --- Replies (lazy) ---
      .addCase(fetchDiscussionReplies.pending, (state, action) => {
        const { rootId } = action.meta.arg;
        const slot = state.repliesByRoot[rootId] || emptySlot();
        slot.loading = true;
        state.repliesByRoot[rootId] = slot;
      })
      .addCase(fetchDiscussionReplies.fulfilled, (state, action) => {
        const { rootId, page } = action.meta.arg;
        const slot = state.repliesByRoot[rootId] || emptySlot();
        const { replies, pagination } = action.payload.data;
        // page > 1 => "Load more" appends; otherwise replace.
        slot.items =
          page && page > 1 ? dedupeById([...slot.items, ...replies]) : dedupeById(replies);
        slot.pagination = pagination;
        slot.loading = false;
        slot.expanded = true;
        state.repliesByRoot[rootId] = slot;
      })
      .addCase(fetchDiscussionReplies.rejected, (state, action) => {
        const { rootId } = action.meta.arg;
        const slot = state.repliesByRoot[rootId] || emptySlot();
        slot.loading = false;
        state.repliesByRoot[rootId] = slot;
      })
      // --- Post (root or reply) ---
      .addCase(postDiscussionMessage.pending, (state) => {
        state.roots.posting = true;
      })
      .addCase(postDiscussionMessage.fulfilled, (state) => {
        // The caller refetches roots (and, for a reply, that root's replies).
        state.roots.posting = false;
      })
      .addCase(postDiscussionMessage.rejected, (state) => {
        state.roots.posting = false;
      });
  },
});

export const { resetDiscussion, collapseReplies } = homeworkDiscussionSlice.actions;

export default homeworkDiscussionSlice.reducer;
